import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ord } from "../api/ord";
import type { CreativeVersion, ManualOrdVerificationCommand, OrdGateState } from "../api/types";

const gateLabels: Record<OrdGateState, string> = {
  not_configured: "Маркировка не зарегистрирована",
  marking_pending: "Регистрация ожидается",
  marking_error: "Ошибка подтверждения маркировки",
  fake: "Тестовая маркировка — публикация запрещена",
  verified: "Подтверждена",
};

export function OrdMarkingPanel({ creativeId, version, onMessage, onGateChange }: {
  creativeId: string;
  version: CreativeVersion;
  onMessage: (message: string, error?: boolean) => void;
  onGateChange: (gate: OrdGateState | null) => void;
}) {
  const cache = useQueryClient();
  const gate = useQuery({
    queryKey: ["ord-gate", version.id],
    queryFn: () => ord.gate(version.id),
  });
  const [erid, setErid] = useState("");
  const [externalReference, setExternalReference] = useState("");
  const [evidenceReference, setEvidenceReference] = useState("");
  const [source, setSource] = useState("");
  const [registeredAt, setRegisteredAt] = useState("");
  const [responsible, setResponsible] = useState("");

  useEffect(() => {
    onGateChange(gate.isSuccess && gate.data ? gate.data.gate : null);
  }, [gate.data, gate.isSuccess, onGateChange]);

  const command = (): ManualOrdVerificationCommand => ({
    erid: erid.trim(),
    externalReference: externalReference.trim(),
    evidenceReference: evidenceReference.trim(),
    source: source.trim(),
    registeredAt: new Date(registeredAt).toISOString(),
    responsible: responsible.trim(),
    evidenceMetadata: {},
  });
  const complete = [erid, externalReference, evidenceReference, source, registeredAt, responsible].every(value => value.trim());
  const verify = useMutation({
    mutationFn: () => ord.manuallyVerify(version.id, command()),
    onSuccess: async () => {
      setErid("");
      setExternalReference("");
      setEvidenceReference("");
      setSource("");
      setRegisteredAt("");
      setResponsible("");
      onMessage(`ORD-маркировка версии v${version.version} подтверждена. Теперь публикацию можно повторить.`);
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["ord-gate", version.id] }),
        cache.invalidateQueries({ queryKey: ["creative", creativeId] }),
      ]);
    },
    onError: () => onMessage("Не удалось записать ORD-подтверждение. Проверьте реальные регистрационные данные.", true),
  });

  if (gate.isLoading) return <div className="ord-marking"><p className="muted">Проверяю ORD-маркировку…</p></div>;
  if (gate.isError || !gate.data) return <div className="ord-marking"><p className="error">Не удалось прочитать состояние ORD.</p></div>;
  const inputId = (name: string) => `ord-${version.id}-${name}`;
  if (gate.data.gate === "verified") {
    return <div className="ord-marking notice"><strong>Маркировка / ORD:</strong> {gateLabels.verified}. ERID сохранён в защищённой записи версии.</div>;
  }

  return <div className="ord-marking">
    <h4>Маркировка / ORD</h4>
    <p className="error"><strong>{gateLabels[gate.data.gate]}.</strong> Без настоящей регистрации и ERID эта версия не публикуется.</p>
    <p className="muted">Сначала зарегистрируйте финальный материал у вашего ОРД. Здесь фиксируются уже полученные внешние доказательства — сервис не создаёт ERID и не подменяет юридическую регистрацию.</p>
    <div className="ord-form">
      <div className="field"><label htmlFor={inputId("erid")}>ERID</label><input id={inputId("erid")} value={erid} maxLength={200} onChange={event => setErid(event.target.value)} placeholder="ERID, выданный ОРД" /></div>
      <div className="field"><label htmlFor={inputId("external-reference")}>Регистрационная ссылка / ID</label><input id={inputId("external-reference")} value={externalReference} maxLength={1000} onChange={event => setExternalReference(event.target.value)} placeholder="ID регистрации во внешнем ОРД" /></div>
      <div className="field"><label htmlFor={inputId("evidence-reference")}>Ссылка / ID доказательства</label><input id={inputId("evidence-reference")} value={evidenceReference} maxLength={1000} onChange={event => setEvidenceReference(event.target.value)} placeholder="Документ, тикет или неизменяемая ссылка" /></div>
      <div className="field"><label htmlFor={inputId("source")}>ОРД / источник</label><input id={inputId("source")} value={source} maxLength={100} onChange={event => setSource(event.target.value)} placeholder="Название внешнего ОРД" /></div>
      <div className="field"><label htmlFor={inputId("registered-at")}>Дата регистрации (часовой пояс этого браузера)</label><input id={inputId("registered-at")} type="datetime-local" value={registeredAt} onChange={event => setRegisteredAt(event.target.value)} /></div>
      <div className="field"><label htmlFor={inputId("responsible")}>Ответственный</label><input id={inputId("responsible")} value={responsible} maxLength={200} onChange={event => setResponsible(event.target.value)} placeholder="Кто проверил внешнее подтверждение" /></div>
    </div>
    <button type="button" disabled={!complete || verify.isPending} onClick={() => {
      if (window.confirm(`Подтвердить настоящую ORD-регистрацию именно для неизменяемой версии v${version.version}? Это действие нельзя отменить.`)) verify.mutate();
    }}>{verify.isPending ? "Сохраняю подтверждение…" : "Подтвердить настоящую ORD-регистрацию"}</button>
  </div>;
}
